import React, { useEffect, useState } from 'react';
import { Banner, UtilityAlertIcon, UtilityChevronIcon } from '@placer-ui/components';
import { SignupEmailStep } from 'features/authentication/signup/components/signup-email-step/signup-email-step';
import {
    SignupFullFormStep,
    SignupFullStepForm,
} from 'features/authentication/signup/components/signup-full-form-step/signup-full-form-step';
import { AuthError } from 'features/authentication/utils/auth-error';
import { useQuery } from 'hooks/use-query/use-query';
import { PlRoute, PlSwitch } from 'router';
import { SignupSuccessStep } from 'features/authentication/signup/components/signup-success-step/signup-success-step';
import { validateEmail } from 'features/authentication/signup/validations/validations';
import { useHistory, useLocation } from 'react-router-dom';
import { SIGNUP_FULL_FORM_STEP_ROUTE } from 'features/authentication/signup/constants/signup-links';
import { useGoBackToEmailStep } from 'features/authentication/signup/hooks/navigation';
import { ERROR_MESSAGES } from 'features/authentication/signup/constants/server-errors';
import styles from './multi-step-signup.module.scss';

export const MultiStepSignup = () => {
    const email = useQuery('email');
    const [userEmail, setUserEmail] = useState(email || '');
    const [userCredentials, setUserCredentials] = useState<Omit<SignupFullStepForm, 'password'>>({
        firstName: '',
        lastName: '',
        company: '',
        phone: '',
    });
    const [serverError, setServerError] = useState<AuthError | undefined>(undefined);
    const location = useLocation();
    const history = useHistory();
    const goBackToEmailStep = useGoBackToEmailStep();

    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const email = queryParams.get('email');
        if (email) {
            const validEmail = validateEmail(email);
            queryParams.delete('email');
            if (validEmail) {
                setUserEmail(email);
                history.replace({
                    pathname: SIGNUP_FULL_FORM_STEP_ROUTE,
                    search: queryParams.toString(),
                });
            } else {
                setUserEmail('');
                history.replace({
                    search: queryParams.toString(),
                });
            }
        }
    }, [history, location.search]);

    const showChevronBackButton = location.pathname === '/auth/signup/access';

    const errorCode = serverError?.status;
    const showErrorBanner = errorCode && errorCode in ERROR_MESSAGES;

    return (
        <>
            {showErrorBanner && (
                <div className={styles.bannerContainer}>
                    <Banner
                        LeftIcon={<UtilityAlertIcon />}
                        text={ERROR_MESSAGES[errorCode]}
                        type="error"
                    />
                </div>
            )}
            <div className={styles.root}>
                {showChevronBackButton && (
                    <div className={styles.header}>
                        <div className={styles.mobileBackButton} onClick={goBackToEmailStep}>
                            <UtilityChevronIcon direction="right" />
                        </div>
                    </div>
                )}
                <div className={styles.signUpScreens}>
                    <PlSwitch>
                        <PlRoute
                            path="/confirm"
                            component={() => <SignupSuccessStep userEmail={userEmail} />}
                        />
                        <PlRoute
                            path="/access"
                            component={() => (
                                <SignupFullFormStep
                                    serverError={serverError}
                                    setServerError={setServerError}
                                    userEmail={userEmail}
                                    userCredentials={userCredentials}
                                    setUserCredentials={setUserCredentials}
                                />
                            )}
                        />
                        <PlRoute
                            path="/"
                            component={() => (
                                <SignupEmailStep
                                    userEmail={userEmail}
                                    setUserEmail={setUserEmail}
                                    serverError={serverError}
                                    setServerError={setServerError}
                                />
                            )}
                        />
                    </PlSwitch>
                </div>
            </div>
        </>
    );
};
